import { useState, useEffect, useRef } from "react";

const API_BASE = "https://radioin-backend-production.up.railway.app/api";
const WS_BASE  = "wss://radioin-backend-production.up.railway.app";
const RELOAD_INTERVAL = 2 * 60 * 1000; // 2 min

function EqualizerBars({ playing }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "3px", height: "32px" }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} style={{
          width: "4px", borderRadius: "2px",
          background: "linear-gradient(to top, #00ff87, #00d4ff)",
          animation: playing ? `eq${i} ${0.4 + i * 0.1}s ease-in-out infinite alternate` : "none",
          height: playing ? undefined : "4px",
          transformOrigin: "bottom",
        }} />
      ))}
      <style>{`
        @keyframes eq1 { from { height: 6px } to { height: 28px } }
        @keyframes eq2 { from { height: 14px } to { height: 20px } }
        @keyframes eq3 { from { height: 8px } to { height: 32px } }
        @keyframes eq4 { from { height: 18px } to { height: 12px } }
        @keyframes eq5 { from { height: 10px } to { height: 26px } }
      `}</style>
    </div>
  );
}

function ProgressRing({ progress, size = 220, stroke = 6 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (progress / 100) * circ;
  return (
    <svg width={size} height={size} style={{ position: "absolute", top: 0, left: 0, transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="url(#grad)" strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.3s ease" }} />
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00ff87" />
          <stop offset="100%" stopColor="#00d4ff" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── Horário de funcionamento da loja ──────────────────────────────────────
// settings.operating_start / operating_end ("HH:MM") e operating_days ("0,1,...", 0=domingo).
// Sem horário configurado = sempre aberto (comportamento antigo, toca 24h).
// Suporta virar a meia-noite (ex: 18:00 → 02:00; o dia vale pela abertura).
function hhmmToMin(t) {
  const [h, m] = String(t || "").split(":").map(Number);
  return (isNaN(h) || isNaN(m)) ? NaN : h * 60 + m;
}
function isStoreOpenAt(settings, now = new Date()) {
  const start = hhmmToMin(settings?.operating_start), end = hhmmToMin(settings?.operating_end);
  if (isNaN(start) || isNaN(end)) return true;
  const days = String(settings.operating_days || "0,1,2,3,4,5,6").split(",").map(d => d.trim());
  const today = String(now.getDay()), yesterday = String((now.getDay() + 6) % 7);
  const mins = now.getHours() * 60 + now.getMinutes();
  if (start === end) return days.includes(today);
  if (start < end) return days.includes(today) && mins >= start && mins < end;
  return (days.includes(today) && mins >= start) || (days.includes(yesterday) && mins < end);
}
function nextOpeningText(settings, now = new Date()) {
  const start = hhmmToMin(settings?.operating_start);
  if (isNaN(start)) return "";
  const days = String(settings.operating_days || "0,1,2,3,4,5,6").split(",").map(d => d.trim());
  const NOMES = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
  const mins = now.getHours() * 60 + now.getMinutes();
  for (let off = 0; off < 8; off++) {
    const dow = (now.getDay() + off) % 7;
    if (!days.includes(String(dow))) continue;
    if (off === 0 && start <= mins) continue;
    const quando = off === 0 ? "hoje" : off === 1 ? "amanhã" : NOMES[dow];
    return `${quando} às ${settings.operating_start}`;
  }
  return "";
}

function buildQueueWithPools(data, albumPools) {
  const { schedule, announcements, albumTracks } = data;
  if (!schedule || schedule.length === 0) {
    return announcements.map(a => ({
      type: "announcement", id: a.id, title: a.title,
      url: a.bunny_url, subtitle: a.voice_name,
      meta: `${a.duration_seconds || 0}s`, text: a.text_content,
    }));
  }
  const queue = [];
  for (const block of schedule) {
    if (block.type === "album") {
      const tracks = albumTracks[block.album_id] || [];
      if (tracks.length === 0) continue;
      const qty = block.qty || 1;
      if (!albumPools[block.album_id] || albumPools[block.album_id].length < qty) {
        albumPools[block.album_id] = shuffle(tracks);
      }
      for (let i = 0; i < qty; i++) {
        if (albumPools[block.album_id].length === 0) albumPools[block.album_id] = shuffle(tracks);
        const track = albumPools[block.album_id].shift();
        queue.push({
          type: "music", id: track.id + "_" + Math.random(),
          title: track.title, url: track.bunny_url,
          subtitle: track.artist || "Música", meta: "",
          albumName: block.name,
        });
      }
    } else if (block.type === "announcement") {
      const ann = (announcements || []).find(a => a.id === block.announcement_id);
      if (!ann) continue;
      const now = new Date();
      if (block.valid_from && now < new Date(block.valid_from)) continue;
      if (block.valid_until && now > new Date(block.valid_until)) continue;
      queue.push({
        type: "announcement", id: ann.id, title: ann.title,
        url: ann.bunny_url, subtitle: ann.voice_name,
        meta: `${ann.duration_seconds || 0}s`, text: ann.text_content,
      });
    } else if (block.type === "announcement_pool") {
      // Bloco especial: marcador — o player busca a locução real via API no momento de tocar
      queue.push({
        type: "announcement_pool",
        id: "pool_" + Math.random(),
        title: block.name || "🎙 Locução",
        category_id: block.category_id || null,
        url: null, subtitle: "rodízio automático", meta: "",
      });
    }
  }
  return queue;
}

function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  // Se está rodando dentro de um iframe, desregistra SW imediatamente e não registra
  if (window.parent !== window) {
    navigator.serviceWorker.getRegistrations()
      .then(regs => regs.forEach(r => r.unregister()))
      .catch(() => {});
    // Impede qualquer SW de se ativar
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      navigator.serviceWorker.getRegistrations()
        .then(regs => regs.forEach(r => r.unregister()))
        .catch(() => {});
    });
    console.log("[RadioIN] Rodando em iframe — SW desativado");
    return;
  }
  navigator.serviceWorker
    .register("/service-worker.js")
    .catch((err) => console.warn("SW registro falhou:", err));
}

function preCacheAudioUrls(urls) {
  if (!navigator.serviceWorker?.controller) return;
  // Filtra apenas músicas (evita erro CORS nas locuções)
  const musicUrls = urls.filter(u => u && u.includes("/musicas/"));
  if (musicUrls.length === 0) return;
  navigator.serviceWorker.controller.postMessage({ type: "PRE_CACHE_AUDIO", urls: musicUrls });
}

export default function Player() {
  const [store, setStore]               = useState(null);
  const [queue, setQueue]               = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying]           = useState(false);
  const [progress, setProgress]         = useState(0);
  const [volume, setVolume]             = useState(0.8);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [currentTime, setCurrentTime]   = useState(0);
  const [duration, setDuration]         = useState(0);
  const [pulse, setPulse]               = useState(false);
  const [reloadCountdown, setReloadCountdown] = useState(RELOAD_INTERVAL / 1000);
  const [isOnline, setIsOnline]         = useState(navigator.onLine);
  const [cachedCount, setCachedCount]   = useState(0);
  const [wsToken, setWsToken]           = useState(null);
  const [autoStarted, setAutoStarted]   = useState(false); // controla tela de splash

  const albumPools      = useRef({});
  const rawData         = useRef(null);
  const audioRef        = useRef(null);
  const currentIndexRef = useRef(0);
  const settingsRef     = useRef({ volume_music: 0.8, volume_announcements: 1.0 }); // já em 0–1
  const masterVolumeRef = useRef(1.0); // volume master do remoto (0–1)
  const settingsReady   = useRef(false); // true após primeiro fetchData com settings do servidor
  const playingRef      = useRef(false);
  const volumeRef       = useRef(0.8);
  const lastProgressRef = useRef({ time: 0, ts: Date.now() }); // watchdog anti-travamento
  const wsRef                  = useRef(null);
  const pingRef                = useRef(null);
  const lastPongRef            = useRef(Date.now());      // último sinal vivo do servidor
  const wsOpenTimeoutRef       = useRef(null);            // aborta conexão travada
  const wsWatchdogRef          = useRef(null);            // vigia pong periódico
  const wsDisconnectedSinceRef = useRef(null);            // desde quando sem WS viva
  const wsReconnectAttemptRef  = useRef(0);               // contador backoff
  const isPlayingAnnouncement  = useRef(false);
  const pendingSwitchRef       = useRef(false); // troca de grade pendente na próxima música
  const queueRef    = useRef([]);   // espelho de queue sem closure stale
  const goNextRef   = useRef(null); // ref estável — listener nunca precisa ser recriado
  const closedRef   = useRef(null); // null=ainda não avaliado | true=fora do horário | false=aberto
  const [isClosed, setIsClosed] = useState(false);
  const isClosedNow = () => !isStoreOpenAt(rawData.current?.store?.settings);

  // ─── Histórico de execução ───────────────────────────────────────────────
  // Cada item (música, locução, pausa) é enviado ao servidor QUANDO TERMINA, com
  // horários do aparelho. Se a conexão cair, fica numa fila e vai quando reconectar.
  const logQueueRef     = useRef([]);   // itens prontos aguardando envio
  const curLogRef       = useRef(null); // item tocando agora
  const suspendedLogRef = useRef(null); // música interrompida por locução (volta depois)
  const nextMetaRef     = useRef(null); // dados da locução que vai interromper (remoto/horário/veículo)
  const poolMetaRef     = useRef(null); // título da locução sorteada no rodízio
  const pauseLogRef     = useRef(null); // pausa em andamento (remoto / fora do horário)

  function flushLog() {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    while (logQueueRef.current.length) {
      const it = logQueueRef.current[0];
      try { ws.send(JSON.stringify({ type: "play_log", item: it })); logQueueRef.current.shift(); }
      catch { break; }
    }
  }
  function queueLog(item) {
    logQueueRef.current.push(item);
    if (logQueueRef.current.length > 500) logQueueRef.current.shift();
    flushLog();
  }
  function closeLog(completed) {
    const c = curLogRef.current;
    if (!c) return;
    curLogRef.current = null;
    const end = c.pauseAt || Date.now();
    const a = audioRef.current;
    const dur = c.duration || (a && a.currentSrc === c.src && isFinite(a.duration) ? Math.round(a.duration) : null);
    queueLog({ kind: c.kind, title: c.title, artist: c.artist, album: c.album, started: c.started, ended: end,
      duration: dur, played: Math.max(0, Math.round((end - c.started - (c.pausedMs || 0)) / 1000)), completed: !!completed });
  }
  function startPauseLog(reason) {
    if (pauseLogRef.current) return;
    pauseLogRef.current = { title: reason, started: Date.now() };
  }
  function endPauseLog() {
    const p = pauseLogRef.current;
    if (!p) return;
    pauseLogRef.current = null;
    const end = Date.now();
    if (end - p.started < 3000) return; // ignora pausas de poucos segundos
    queueLog({ kind: "pausa", title: p.title, started: p.started, ended: end, played: Math.round((end - p.started) / 1000), completed: true });
  }
  function kindFromTitle(title, logKind) {
    if (logKind) return logKind;
    const t = String(title || "");
    if (t.includes("🚗")) return "veiculo";
    if (t.includes("Locução ao vivo")) return "tts";
    if (t.includes("Voz ao vivo")) return "voz";
    return "locucao";
  }

  // Pausa o player por estar fora do horário (sem baixar nada do Bunny enquanto pausado)
  function pauseForClosed(audio) {
    startPauseLog("Fora do horário de funcionamento");
    closedRef.current = true; setIsClosed(true);
    if (audio) {
      audio.pause();
      audio.muted = false; // garante som na reabertura (autoplay inicia mutado)
      lastProgressRef.current = { time: 0, ts: Date.now() };
    }
    playingRef.current = false; setPlaying(false);
  }

  // Atualiza state + ref juntos
  function setQueueAndRef(q) { queueRef.current = q; setQueue(q); }

  // Volume real = base do painel × master do remoto
  function applyVolume(audio, type) {
    // 'announcement_pool' = locução do rodízio de categoria — também é locução
    const base = (type === 'announcement' || type === 'announcement_pool')
      ? settingsRef.current.volume_announcements
      : settingsRef.current.volume_music;
    const real = Math.min(1, base * masterVolumeRef.current);
    audio.volume = real;
    volumeRef.current = real;
    setVolume(real);
  }

  const storeId = new URLSearchParams(window.location.search).get("store");
  // Autoplay é padrão TRUE (funciona no Fully Kiosk + muted-unmute trick nos outros).
  // Pra desabilitar, passa ?autoplay=0 na URL.
  const autoplayParam = new URLSearchParams(window.location.search).get("autoplay") !== "0"
    && new URLSearchParams(window.location.search).get("autoplay") !== "false"
    || new URLSearchParams(window.location.search).get("play") === "1";

  // Service Worker
  useEffect(() => {
    registerServiceWorker();
    const onMessage = (event) => {
      if (event.data?.type === "CACHE_UPDATED" || event.data?.type === "CACHE_STATUS_RESULT") {
        setCachedCount(event.data.count || 0);
      }
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
  }, []);

  async function fetchData(isReload = false) {
    if (!storeId) { setError("ID da loja não informado."); setLoading(false); return; }
    try {
      const res = await fetch(`${API_BASE}/player/${storeId}`);
      const data = await res.json();
      if (data.error) { setError(data.error); setLoading(false); return; }
      setStore(data.store);

      // Salva settings de volume para uso no player
      if (data.store?.settings) {
        settingsRef.current = {
          volume_music: (data.store.settings.volume_music ?? 80) / 100,
          volume_announcements: (data.store.settings.volume_announcements ?? 100) / 100,
        };
        // Volume master do remoto salvo na loja — restaura só na 1ª carga
        // (nas recargas de 2 min o valor em memória já é o mais recente)
        if (!isReload && typeof data.store.settings.remote_volume === "number") {
          masterVolumeRef.current = Math.max(0, Math.min(1, data.store.settings.remote_volume / 100));
        }
        settingsReady.current = true;
        // Aplica volume correto imediatamente na faixa que já está tocando
        if (audioRef.current && !isPlayingAnnouncement.current) {
          const ct = queueRef.current[currentIndexRef.current];
          applyVolume(audioRef.current, ct?.type || 'music');
        }
      }

      if (!isReload) {
        // Primeira carga — monta a fila normalmente
        rawData.current = data;
        const q = buildQueueWithPools(data, albumPools.current);
        setQueueAndRef(q);
        setLoading(false);
        if (data.wsToken) setWsToken(data.wsToken);
      } else {
        // Reload — verifica se a grade mudou comparando JSON da schedule
        const oldSchedule = JSON.stringify(rawData.current?.schedule || []);
        const newSchedule = JSON.stringify(data.schedule || []);
        rawData.current = data;

        if (oldSchedule !== newSchedule) {
          albumPools.current = {};
          const newQ = buildQueueWithPools(data, albumPools.current);

          if (newQ.length > 0) {
            // queueRef.current nunca sofre closure stale
            const currentTrack = queueRef.current[currentIndexRef.current];
            const currentInNew = newQ.findIndex(t => t.url === currentTrack?.url);

            if (currentInNew !== -1) {
              // Faixa atual existe na nova grade — mantém índice, não muda áudio
              setQueueAndRef(newQ);
              currentIndexRef.current = currentInNew;
            } else {
              // Faixa atual não existe mais — insere na frente, player termina ela e segue
              const merged = currentTrack ? [currentTrack, ...newQ] : newQ;
              setQueueAndRef(merged);
            }
          }
        }
      }

      // Pré-cache SÓ das próximas músicas da fila (não do acervo inteiro — isso
      // fazia o aparelho baixar tudo em loop e gastava muito tráfego no Bunny)
      const audioUrls = [];
      const fila = queueRef.current || [];
      const ini = currentIndexRef.current || 0;
      for (let i = ini; i < fila.length && audioUrls.length < 6; i++) {
        if (fila[i]?.type === "music" && fila[i].url && !audioUrls.includes(fila[i].url)) audioUrls.push(fila[i].url);
      }
      // Fora do horário não pré-baixa nada (economia de Bunny)
      if (isStoreOpenAt(data.store?.settings)) navigator.serviceWorker?.ready.then(() => preCacheAudioUrls(
        audioUrls.filter(u => u && u.includes("/musicas/"))
      ));
    } catch {
      if (!isReload) { setError("Erro ao conectar com o servidor."); setLoading(false); }
    }
  }

  useEffect(() => { fetchData(false); }, [storeId]);

  useEffect(() => {
    const interval = setInterval(() => fetchData(true), RELOAD_INTERVAL);
    return () => clearInterval(interval);
  }, [storeId]);

  // ─── Locuções em horário fixo (avisos de encerramento, abertura etc) ─────
  // A cada 15s compara o relógio do dispositivo com schedule_times das locuções.
  // No minuto certo, interrompe a música, toca a locução e volta de onde parou
  // (mesmo mecanismo do play_announcement do remoto). Cada horário toca 1x por dia.
  const scheduledPlayedRef = useRef(new Set());
  useEffect(() => {
    const check = () => {
      const anns = rawData.current?.announcements || [];
      if (!anns.length || !audioRef.current) return;
      if (isPlayingAnnouncement.current) return; // outra locução interrompendo — tenta no próximo ciclo
      const now = new Date();
      const hhmm = String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0");
      const dow = String(now.getDay()); // 0=domingo
      const dateKey = now.getFullYear() + "-" + (now.getMonth() + 1) + "-" + now.getDate();
      for (const a of anns) {
        if (!a.schedule_times || !a.bunny_url) continue;
        if (!a.schedule_times.split(",").map(s => s.trim()).includes(hhmm)) continue;
        if (!(a.schedule_days || "0,1,2,3,4,5,6").split(",").includes(dow)) continue;
        const key = a.id + "|" + dateKey + "|" + hhmm;
        if (scheduledPlayedRef.current.has(key)) continue;
        scheduledPlayedRef.current.add(key);
        console.log("[RadioIN] ⏰ Locução programada:", a.title, hhmm);
        handleRemoteCommand({ action: "play_announcement", url: a.bunny_url, title: a.title, logKind: "aviso" });
        break; // uma por vez — se houver outra no mesmo minuto, entra no próximo ciclo
      }
    };
    const t = setInterval(check, 15000);
    return () => clearInterval(t);
  }, []);

  // ─── Horário de funcionamento ────────────────────────────────────────────
  // A cada 15s verifica se a loja está aberta. Age só na MUDANÇA de estado:
  //  aberto → fechado: pausa (se houver locução tocando, espera ela terminar)
  //  fechado → aberto: volta a tocar sozinho
  // Se alguém der play manual fora do horário, respeita até a próxima mudança.
  useEffect(() => {
    const check = () => {
      if (!rawData.current) return;
      const closed = isClosedNow();
      if (closed === closedRef.current) return;
      const audio = audioRef.current;
      if (closed) {
        if (isPlayingAnnouncement.current) return; // espera a locução terminar
        console.log("[RadioIN] 🌙 Fora do horário de funcionamento — pausando");
        pauseForClosed(audio);
      } else {
        const wasClosed = closedRef.current === true;
        closedRef.current = false; setIsClosed(false);
        if (wasClosed && audio && audio.src && !playingRef.current) {
          console.log("[RadioIN] ☀️ Horário de funcionamento — voltando a tocar");
          endPauseLog();
          lastProgressRef.current = { time: 0, ts: Date.now() };
          const ct = queueRef.current[currentIndexRef.current];
          if (settingsReady.current) applyVolume(audio, ct?.type || "music");
          audio.play().then(() => { playingRef.current = true; setPlaying(true); }).catch(() => {});
        }
      }
      setTimeout(() => sendPlayerState(), 300);
    };
    const first = setTimeout(check, 3000);
    const t = setInterval(check, 15000);
    return () => { clearTimeout(first); clearInterval(t); };
  }, []);

  useEffect(() => {
    setReloadCountdown(RELOAD_INTERVAL / 1000);
    const tick = setInterval(() => setReloadCountdown(p => p <= 1 ? RELOAD_INTERVAL / 1000 : p - 1), 1000);
    return () => clearInterval(tick);
  }, []);

  // Online/offline
  useEffect(() => {
    const handleOffline = () => setIsOnline(false);
    const handleOnline = () => {
      setIsOnline(true);
      fetchData(false);
      const audio = audioRef.current;
      if (audio && playingRef.current) { audio.load(); audio.play().catch(() => {}); }
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  // ── WebSocket — conecta ao backend como "player" ──────────────────────────
  // Guarda contra vazamento de conexões:
  //  - cleanupWS() remove handlers ANTES de close() para evitar que ws.onclose
  //    dispare uma reconexão fantasma do WS antigo.
  //  - scheduleReconnect() com flag `reconnectInFlight` garante 1 reconexão
  //    agendada por vez, mesmo com múltiplas rotas de disparo (onclose, watchdog,
  //    visibilitychange, focus, online).
  //  - Identity check (wsRef.current !== ws) faz callbacks de WS antigos
  //    virarem no-ops, blindando contra close() assíncrono.
  //  - Web Worker heartbeat preservado (evita throttling de iframe em FireStick).
  //  - Watchdog 30s idle → forceReconnect; 3min disconnected → reload().
  useEffect(() => {
    if (!storeId || !wsToken) return;

    let dead = false;
    let reconnectTimer = null;
    let reconnectInFlight = false;

    const BACKOFF_MS = [2000, 4000, 8000, 15000, 30000];

    function cleanupHeartbeat() {
      if (pingRef.current) {
        if (typeof pingRef.current.terminate === 'function') {
          pingRef.current.terminate();
        } else {
          clearInterval(pingRef.current);
        }
        pingRef.current = null;
      }
      if (wsOpenTimeoutRef.current) {
        clearTimeout(wsOpenTimeoutRef.current);
        wsOpenTimeoutRef.current = null;
      }
    }

    function cleanupWS() {
      const ws = wsRef.current;
      if (!ws) return;
      // CRUCIAL: remover handlers antes de close() impede que ws.onclose
      // dispare uma nova reconexão. Essa era a fonte do vazamento.
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try {
        if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      } catch {}
      wsRef.current = null;
    }

    function scheduleReconnect() {
      if (dead || reconnectInFlight) return;
      reconnectInFlight = true;
      wsReconnectAttemptRef.current += 1;
      const idx = Math.min(wsReconnectAttemptRef.current - 1, BACKOFF_MS.length - 1);
      const delay = BACKOFF_MS[idx];
      console.log(`[RadioIN WS] Reconectando em ${delay/1000}s (tentativa ${wsReconnectAttemptRef.current})`);
      reconnectTimer = setTimeout(() => {
        reconnectInFlight = false;
        connect();
      }, delay);
    }

    function forceReconnect(reason) {
      if (dead) return;
      if (reason) console.warn(`[RadioIN WS] forceReconnect: ${reason}`);
      cleanupHeartbeat();
      cleanupWS();
      if (!wsDisconnectedSinceRef.current) wsDisconnectedSinceRef.current = Date.now();
      scheduleReconnect();
    }

    function connect() {
      if (dead) return;
      cleanupHeartbeat();
      cleanupWS(); // garantia: nada velho pendurado

      const url = `${WS_BASE}/ws/remote?store=${storeId}&token=${wsToken}&role=player`;
      console.log("[RadioIN WS] Conectando:", url.replace(wsToken, "***"));
      const ws = new WebSocket(url);
      wsRef.current = ws;

      // Se o onopen não disparar em 15s, trata como falha
      wsOpenTimeoutRef.current = setTimeout(() => {
        if (wsRef.current === ws && ws.readyState !== WebSocket.OPEN) {
          console.warn("[RadioIN WS] Timeout de conexão — forçando reconexão");
          forceReconnect("timeout de abertura");
        }
      }, 15000);

      ws.onopen = () => {
        if (dead || wsRef.current !== ws) { try { ws.close(); } catch {} return; }
        if (wsOpenTimeoutRef.current) { clearTimeout(wsOpenTimeoutRef.current); wsOpenTimeoutRef.current = null; }
        console.log("[RadioIN WS] ✅ Conectado como player");
        setTimeout(flushLog, 1000); // envia o histórico que ficou na fila
        wsReconnectAttemptRef.current = 0;
        wsDisconnectedSinceRef.current = null;
        lastPongRef.current = Date.now();

        // Web Worker para enviar pings — roda em thread separada, sem throttling
        // do browser quando o iframe está em background (FireStick, Chromium)
        try {
          const workerCode = `
            let pingTimer = null;
            self.onmessage = function(e) {
              if (e.data === 'start') {
                if (pingTimer) clearInterval(pingTimer);
                pingTimer = setInterval(() => self.postMessage('ping'), 8000);
              } else if (e.data === 'stop') {
                if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
              }
            };
          `;
          const blob = new Blob([workerCode], { type: 'application/javascript' });
          const worker = new Worker(URL.createObjectURL(blob));
          worker.onmessage = () => {
            if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
            }
          };
          worker.postMessage('start');
          pingRef.current = worker; // guarda o worker para terminar depois
          console.log("[RadioIN WS] Web Worker iniciado para heartbeat");
        } catch (err) {
          // Fallback para setInterval se Web Worker falhar
          console.warn("[RadioIN WS] Worker falhou, usando setInterval:", err);
          pingRef.current = setInterval(() => {
            if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
            }
          }, 8000);
        }
        sendPlayerState(ws);
      };

      ws.onmessage = (e) => {
        if (dead || wsRef.current !== ws) return;
        // Qualquer mensagem do servidor é prova de que a conexão está viva
        lastPongRef.current = Date.now();
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "pong") return;        // pong do servidor, só confirma vida
          if (msg.type !== "cmd") return;
          handleRemoteCommand(msg);
        } catch {}
      };

      ws.onclose = (e) => {
        if (dead) return;
        if (wsRef.current !== ws) return; // outro WS já tomou o lugar
        console.warn("[RadioIN WS] Desconectado — código:", e.code, "motivo:", e.reason);
        cleanupHeartbeat();
        wsRef.current = null;
        if (!wsDisconnectedSinceRef.current) wsDisconnectedSinceRef.current = Date.now();
        scheduleReconnect();
      };

      ws.onerror = (e) => {
        // Não chama close() aqui — deixa o onclose cuidar da reconexão.
        // Chamar close() aqui causava race condition de reconexão dupla.
        console.error("[RadioIN WS] Erro:", e);
      };
    }

    connect();

    // Watchdog: força reconexão se não recebermos nenhuma mensagem do servidor
    // há mais de 30s (servidor manda ping WS a cada 20s; se TCP está vivo,
    // o ws deveria pelo menos responder ao nosso ping de app com pong).
    // Se ficarmos desconectados por mais de 3 min, recarrega a página inteira
    // (último recurso contra bugs de throttling em FireStick após muitas horas).
    wsWatchdogRef.current = setInterval(() => {
      if (dead) return;
      const agora = Date.now();
      const ws = wsRef.current;
      const idle = agora - (lastPongRef.current || 0);

      if (ws && ws.readyState === WebSocket.OPEN && idle > 30000) {
        forceReconnect(`sem sinal do servidor há ${Math.round(idle/1000)}s`);
        return;
      }

      if (wsDisconnectedSinceRef.current && (agora - wsDisconnectedSinceRef.current) > 3 * 60 * 1000) {
        console.error("[RadioIN WS] Desconectado há >3min — recarregando página");
        try { window.location.reload(); } catch {}
      }
    }, 10000);

    // Reconecta imediatamente quando aba volta ao foco (browser throttla em background)
    const onVisible = () => {
      if (dead) return;
      if (document.visibilityState !== 'visible') return;
      const ws = wsRef.current;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        // Passa pelo scheduleReconnect (que tem guard contra duplicação)
        if (!reconnectInFlight) {
          console.log("[RadioIN WS] Reconectando após voltar ao foco...");
          forceReconnect("voltou ao foco com WS fechado");
        }
      } else if (ws.readyState === WebSocket.OPEN) {
        // Força ping imediato para validar a conexão
        try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('online', onVisible);

    return () => {
      dead = true;
      clearTimeout(reconnectTimer);
      cleanupHeartbeat();
      if (wsWatchdogRef.current) { clearInterval(wsWatchdogRef.current); wsWatchdogRef.current = null; }
      cleanupWS();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [storeId, wsToken]);

  // Envia estado atual do player para os remotos
  function sendPlayerState(ws) {
    const current = queue[currentIndexRef.current];
    const stateData = {
      title: current?.title || "",
      artist: current?.subtitle || "",
      track: current?.title || "",
      playing: playingRef.current,
      volume: Math.round(masterVolumeRef.current * 100),
    };

    // Notifica o pai (launcher/IndoorCast) via postMessage — independente do WebSocket
    try {
      if (window.parent !== window) {
        window.parent.postMessage(JSON.stringify({
          type: "player_state",
          data: stateData,
        }), "*");
      }
    } catch(e) {}

    // Envia via WebSocket se conectado
    const w = ws || wsRef.current;
    if (!w || w.readyState !== 1) return;
    w.send(JSON.stringify({ type: "player_state", data: stateData }));
  }

  // Executa comandos recebidos do controle remoto
  function handleRemoteCommand(msg) {
    const audio = audioRef.current;
    if (!audio) return;

    if (msg.action === "toggle") {
      if (playingRef.current) {
        audio.pause(); setPlaying(false); playingRef.current = false;
        startPauseLog("Pausado pelo remoto");
      } else {
        endPauseLog();
        audio.play().then(() => { setPlaying(true); playingRef.current = true; }).catch(() => {});
      }
      setTimeout(() => sendPlayerState(), 200);
    }

    if (msg.action === "skip") {
      goNext();
      setTimeout(() => sendPlayerState(), 200);
    }

    if (msg.action === "volume" && typeof msg.value === "number") {
      const v = Math.max(0, Math.min(1, msg.value));
      masterVolumeRef.current = v;
      const ct = queueRef.current[currentIndexRef.current];
      applyVolume(audio, ct?.type || 'music');
      setTimeout(() => sendPlayerState(), 100);
    }

    if (msg.action === "switch_schedule") {
      // Troca de grade — recarrega dados do player
      if (msg.immediate) {
        // Troca imediata — para o áudio atual e recarrega
        audio.pause();
        fetchData(false); // recarrega do zero com a nova grade ativa
      } else {
        // Troca na próxima música — flag para recarregar após a faixa atual
        pendingSwitchRef.current = true;
      }
    }

    if (msg.action === "play_announcement" && msg.url) {
      nextMetaRef.current = { kind: kindFromTitle(msg.title, msg.logKind), title: String(msg.title || "Locução").replace(/^[🚗🔁🎤🎙\s]+/u, "").trim() || "Locução" };
      const wasPlaying = playingRef.current;
      const prevSrc    = audio.src;
      const prevTime   = audio.currentTime;
      isPlayingAnnouncement.current = true;
      audio.pause();

      audio.src = msg.url;
      // Volume da locução respeitando o master do remoto
      audio.volume = Math.min(1, settingsRef.current.volume_announcements * masterVolumeRef.current);

      audio.play().then(() => {
        setPlaying(true); playingRef.current = true;
        setPulse(true); setTimeout(() => setPulse(false), 600);
      }).catch(() => {
        isPlayingAnnouncement.current = false;
        audio.src = prevSrc;
        audio.currentTime = prevTime;
        const ct = queueRef.current[currentIndexRef.current];
        applyVolume(audio, ct?.type || 'music');
        if (wasPlaying) audio.play().catch(() => {});
      });

      audio.onended = null;

      audio.onended = () => {
        isPlayingAnnouncement.current = false;
        audio.src = prevSrc;
        audio.currentTime = prevTime;
        // Restaura volume correto da música × master
        const ct = queueRef.current[currentIndexRef.current];
        applyVolume(audio, ct?.type || 'music');
        // Se a loja fechou durante a locução (ex: aviso final de encerramento), não volta a música
        if (wasPlaying && isClosedNow()) {
          pauseForClosed(audio);
        } else if (wasPlaying) {
          audio.play()
            .then(() => { setPlaying(true); playingRef.current = true; })
            .catch(() => {});
        } else {
          setPlaying(false); playingRef.current = false;
        }
        audio.onended = null;
        setTimeout(() => sendPlayerState(), 200);
      };

      setTimeout(() => sendPlayerState(), 200);
    }
  }

  // ─── Tenta autoplay silencioso — engana bloqueio do browser ─────────────
  function attemptAutoplay() {
    const audio = audioRef.current;
    if (!audio || playingRef.current) return;
    audio.volume = 0;
    audio.play()
      .then(() => {
        setPlaying(true);
        playingRef.current = true;
        setAutoStarted(true);
        // Sobe volume gradualmente até o valor correto (base × master)
        const ct = queueRef.current[currentIndexRef.current];
        const base = (ct?.type === 'announcement' || ct?.type === 'announcement_pool')
          ? settingsRef.current.volume_announcements
          : settingsRef.current.volume_music;
        const target = Math.min(1, base * masterVolumeRef.current);
        let v = 0;
        const step = setInterval(() => {
          v = Math.min(v + 0.05, target);
          audio.volume = v;
          if (v >= target) { volumeRef.current = target; clearInterval(step); }
        }, 80);
      })
      .catch(() => {
        setAutoStarted(false);
      });
  }

  function handleSplashClick() {
    const audio = audioRef.current;
    if (!audio) return;
    const first = queue[0];
    if (!first) return;

    // Seta src se necessário
    if (!audio.src || audio.src === window.location.href) {
      audio.src = first.url;
      audio.volume = volumeRef.current || 0.8;
    }

    // Toca diretamente — não depende do useEffect
    audio.play()
      .then(() => {
        playingRef.current = true;
        setPlaying(true);
        setAutoStarted(true);
        // Fora do horário: o clique só libera o áudio; volta a tocar na abertura
        if (isClosedNow()) pauseForClosed(audio);
      })
      .catch(() => {
        // Se falhar mesmo com clique, mostra o player para tentar pelo botão
        playingRef.current = false;
        setAutoStarted(true);
      });
  }

  async function resolveAnnouncementPool(categoryId) {
    try {
      const res = await fetch(`${API_BASE}/player/${storeId}/next-announcement`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categoryId: categoryId || null }),
      });
      const data = await res.json();
      return data.announcement || null;
    } catch {
      return null;
    }
  }

  function goNext() {
    if (isPlayingAnnouncement.current) return;
    setPulse(true);
    setTimeout(() => setPulse(false), 600);

    // Troca de grade pendente — recarrega antes de avançar
    if (pendingSwitchRef.current) {
      pendingSwitchRef.current = false;
      fetchData(false);
      return;
    }

    function advanceTo(nextIndex) {
      const q = queueRef.current;
      const nextItem = q[nextIndex];

      // Bloco pool — resolve via API e toca ou pula
      if (nextItem?.type === "announcement_pool") {
        setCurrentIndex(nextIndex); currentIndexRef.current = nextIndex;
        resolveAnnouncementPool(nextItem.category_id).then(ann => {
          if (!ann) {
            // Nenhuma locução disponível — pula para o próximo
            const skip = nextIndex + 1 < q.length ? nextIndex + 1 : 0;
            if (skip === 0 && rawData.current) {
              const newQ = buildQueueWithPools(rawData.current, albumPools.current);
              setQueueAndRef(newQ);
            }
            setCurrentIndex(skip); currentIndexRef.current = skip;
            return;
          }
          // Toca a locução resolvida
          const audio = audioRef.current;
          if (!audio) return;
          poolMetaRef.current = { title: ann.title };
          audio.src = ann.bunny_url;
          if (settingsReady.current) applyVolume(audio, "announcement");
          if (playingRef.current) audio.play().catch(() => {});
        });
        return;
      }

      setCurrentIndex(nextIndex); currentIndexRef.current = nextIndex;
    }

    const nextIndex = currentIndexRef.current + 1;
    if (nextIndex >= queueRef.current.length) {
      if (rawData.current) {
        const newQ = buildQueueWithPools(rawData.current, albumPools.current);
        setQueueAndRef(newQ);
      }
      advanceTo(0);
    } else {
      advanceTo(nextIndex);
    }
  }
  goNextRef.current = goNext;

  const current = queue[currentIndex];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      const ct = audio.currentTime;
      setCurrentTime(ct);
      setDuration(audio.duration || 0);
      setProgress(audio.duration ? (ct / audio.duration) * 100 : 0);
      // Atualiza watchdog — o tempo avançou, player está vivo
      lastProgressRef.current = { time: ct, ts: Date.now() };
    };
    const onEnded = () => goNextRef.current?.();
    const onError = (e) => {
      const code = e?.target?.error?.code;
      // Erro de rede/404 (MEDIA_ERR_NETWORK=2, MEDIA_ERR_SRC_NOT_SUPPORTED=4)
      // ou qualquer erro quando não é locução do remoto — pula para a próxima
      if (!isPlayingAnnouncement.current) {
        console.warn(`[RadioIN] Erro de áudio (code=${code}) — pulando faixa automaticamente`);
        lastProgressRef.current = { time: 0, ts: Date.now() };
        setTimeout(() => goNextRef.current?.(), 1000);
      } else {
        // Era locução do remoto — apenas encerra
        isPlayingAnnouncement.current = false;
      }
    };
    // ── Histórico: início / pausa / fim de cada item ──
    const onPlayingLog = () => {
      const src = audio.currentSrc || audio.src;
      if (!src || src === window.location.href) return;
      const now = Date.now();
      const resume = (c) => { if (c.pauseAt) { c.pausedMs = (c.pausedMs || 0) + (now - c.pauseAt); c.pauseAt = null; } };
      const c = curLogRef.current;
      if (c && c.src === src) { resume(c); return; }                 // retomou o mesmo item
      const sp = suspendedLogRef.current;
      if (!c && sp && sp.src === src && !isPlayingAnnouncement.current) { // voltou para a música interrompida
        suspendedLogRef.current = null; curLogRef.current = sp; resume(sp); return;
      }
      if (c) {
        if (isPlayingAnnouncement.current && c.kind === "musica") { c.pauseAt = c.pauseAt || now; suspendedLogRef.current = c; curLogRef.current = null; }
        else closeLog(false);                                            // trocou antes de terminar (pulou)
      }
      let meta;
      if (isPlayingAnnouncement.current) {
        meta = nextMetaRef.current || { kind: "locucao", title: "Locução" };
        nextMetaRef.current = null;
      } else {
        const ct = queueRef.current[currentIndexRef.current] || {};
        if (ct.type === "music") meta = { kind: "musica", title: ct.title, artist: ct.subtitle, album: ct.albumName };
        else if (ct.type === "announcement_pool") meta = { kind: "locucao", title: poolMetaRef.current?.title || ct.title };
        else meta = { kind: "locucao", title: ct.title };
      }
      curLogRef.current = { ...meta, src, started: now, pausedMs: 0, pauseAt: null };
    };
    const onPauseLog = () => { const c = curLogRef.current; if (c && !audio.ended && !c.pauseAt) c.pauseAt = Date.now(); };
    const onEndedLog = () => closeLog(true);
    audio.addEventListener("playing", onPlayingLog);
    audio.addEventListener("pause", onPauseLog);
    audio.addEventListener("ended", onEndedLog);

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    // ── Watchdog anti-travamento ─────────────────────────────────────────────
    // Verifica a cada 10s se o player deveria estar tocando mas travou
    const watchdog = setInterval(() => {
      if (!playingRef.current) return; // pausado pelo usuário — normal
      if (isPlayingAnnouncement.current) return; // locução do remoto — normal
      const { time, ts } = lastProgressRef.current;
      const secsStuck = (Date.now() - ts) / 1000;
      // Se o tempo do áudio não avançou em mais de 12s mas deveria estar tocando
      if (secsStuck > 12) {
        console.warn(`[RadioIN] Watchdog: player travado há ${secsStuck.toFixed(0)}s — avançando faixa`);
        lastProgressRef.current = { time: 0, ts: Date.now() };
        goNextRef.current?.();
      }
    }, 10000);

    return () => {
      audio.removeEventListener("playing", onPlayingLog);
      audio.removeEventListener("pause", onPauseLog);
      audio.removeEventListener("ended", onEndedLog);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      clearInterval(watchdog);
    };
  }, []); // listener registrado uma vez — goNextRef sempre atualizado

  // ─── Autoplay automático (Caminho 1: muted → unmute + retries) ─────────────
  // Estratégia:
  //   1. Muted + autoplay (browsers permitem quase sempre)
  //   2. Se sucesso → desmute imediato e sobe volume gradualmente
  //   3. Se falhar → retry 3x com 500ms
  //   4. Se falhar tudo → cai pro splash "TOQUE PARA INICIAR"
  useEffect(() => {
    console.log(`[RadioIN] 🎯 Autoplay useEffect — autoplayParam=${autoplayParam}, queue.length=${queue.length}, autoStarted=${autoStarted}`);
    if (!autoplayParam) { console.log('[RadioIN] pulou: autoplayParam=false'); return; }
    if (queue.length === 0) { console.log('[RadioIN] pulou: queue vazia'); return; }
    if (autoStarted) { console.log('[RadioIN] pulou: autoStarted=true'); return; }

    const audio = audioRef.current;
    if (!audio) { console.log('[RadioIN] pulou: audio ref null'); return; }

    let attempts = 0;
    const maxAttempts = 3;

    const tryAutoplay = () => {
      attempts++;

      // Garante que src está setado — race condition: o outro useEffect que
      // seta src pode não ter rodado ainda. Sem src, play() fica pendurado.
      if (!audio.src || audio.src === window.location.href) {
        const first = queue[0];
        if (!first?.url) {
          console.warn(`[RadioIN] queue[0] sem URL, retry`);
          if (attempts < maxAttempts) return setTimeout(tryAutoplay, 500);
          return setAutoStarted(false);
        }
        audio.src = first.url;
        console.log(`[RadioIN] src setado: ${first.url.substring(0, 60)}...`);
      }

      // Estratégia mais confiável: muted=true (browser considera "sem som")
      audio.muted = true;
      audio.volume = 0;
      audio.play()
        .then(() => {
          console.log(`[RadioIN] Autoplay OK na tentativa ${attempts}`);
          setPlaying(true);
          playingRef.current = true;
          setAutoStarted(true);

          // Fora do horário: autoplay já liberado, pausa e espera a abertura
          if (isClosedNow()) {
            console.log("[RadioIN] 🌙 Iniciou fora do horário — aguardando abertura");
            pauseForClosed(audio);
            return;
          }

          // Desmuta e sobe volume gradualmente (fade-in ~800ms)
          setTimeout(() => {
            audio.muted = false;
            const ct = queueRef.current[currentIndexRef.current];
            const base = (ct?.type === 'announcement' || ct?.type === 'announcement_pool')
              ? settingsRef.current.volume_announcements
              : settingsRef.current.volume_music;
            const target = Math.min(1, base * masterVolumeRef.current);
            let v = 0;
            const step = setInterval(() => {
              v = Math.min(v + 0.05, target);
              audio.volume = v;
              if (v >= target) { volumeRef.current = target; clearInterval(step); }
            }, 80);
          }, 150);
        })
        .catch((err) => {
          console.warn(`[RadioIN] Autoplay bloqueado (tentativa ${attempts}/${maxAttempts}):`, err.message);
          if (attempts < maxAttempts) {
            setTimeout(tryAutoplay, 500);
          } else {
            // Todos os retries falharam — mostra splash pro usuário clicar
            setAutoStarted(false);
          }
        });
    };

    tryAutoplay();
  }, [queue.length, autoplayParam]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !current) return;
    if (isPlayingAnnouncement.current) return;
    // Bloco pool: o src será setado assincronamente pelo goNext após resolver a API
    if (current.type === "announcement_pool") return;
    // Normaliza URLs antes de comparar — browser converte src para URL absoluta
    const norm = (u) => decodeURIComponent((u || "").replace(/\/$/, "").split("?")[0]);
    if (norm(audio.src) === norm(current.url)) return;
    audio.src = current.url;

    // Volume = base do painel × master do remoto (só aplica após settings carregados)
    if (settingsReady.current) applyVolume(audio, current.type);

    if (playingRef.current) {
      audio.play()
        .then(() => { setPlaying(true); })
        .catch(() => {});
    }
    sendPlayerState();
  }, [currentIndex]); // só currentIndex — não depende de current (objeto novo a cada render)

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    volumeRef.current = volume;
  }, [volume]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !current) return;
    if (playing) {
      audio.pause(); setPlaying(false); playingRef.current = false;
      startPauseLog("Pausado no player");
    } else {
      endPauseLog();
      if (!audio.src) audio.src = current.url;
      audio.play().then(() => { setPlaying(true); playingRef.current = true; }).catch(() => {});
    }
    setTimeout(() => sendPlayerState(), 200);
  };

  const playNext = () => goNext();

  const playPrev = () => {
    const prev = Math.max(0, currentIndexRef.current - 1);
    setCurrentIndex(prev); currentIndexRef.current = prev;
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const fmtCountdown = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,"0")}`;
  const formatTime = (s) => { if (!s || isNaN(s)) return "0:00"; const m = Math.floor(s/60); const sec = Math.floor(s%60); return `${m}:${sec.toString().padStart(2,"0")}`; };

  // ── Elemento de áudio ÚNICO — fora de qualquer condicional ──────────────────
  // Isso garante que audioRef.current é sempre o mesmo elemento,
  // independente de qual tela está sendo mostrada (loading, splash, player)
  // Aviso "fora do horário" fica junto do <audio> (que continua sendo o 1º filho — não remonta)
  const closedInfo = isClosed && !playing ? (
    <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 50, padding: "8px 12px", textAlign: "center",
      background: "rgba(5,8,16,0.92)", borderTop: "1px solid rgba(255,170,0,0.4)", color: "#ffbb33",
      fontFamily: "monospace", fontSize: 13, letterSpacing: 1 }}>
      🌙 Fora do horário de funcionamento
      {nextOpeningText(store?.settings) && <> · volta {nextOpeningText(store?.settings)}</>}
    </div>
  ) : null;
  const audioEl = <><audio ref={audioRef} style={{ display: "none" }} />{closedInfo}</>;
  const isInIframe = window.parent !== window;

  // ── Layout horizontal (barra) — quando em iframe ──────────────
  if (isInIframe) {
    const barStyle = {
      width: "100%", height: "100%",
      background: "#050810",
      display: "flex", alignItems: "center",
      gap: 32, padding: "0 32px",
      fontFamily: "monospace",
      overflow: "hidden",
      position: "relative",
    };

    const loadingBar = (msg) => (
      <div style={{ ...barStyle, justifyContent: "center" }}>
        {audioEl}
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#00ff87", display: "inline-block", opacity: 0.5 }} />
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 3, color: "#fff" }}>RADIO<span style={{ color: "#00ff87" }}>IN</span></span>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2 }}>{msg}</span>
        <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
      </div>
    );

    if (loading) return loadingBar("CARREGANDO...");
    if (error) return loadingBar("ERRO DE CONEXÃO");
    if (queue.length === 0) return loadingBar("SEM GRADE CONFIGURADA");

    // Splash horizontal — sempre clicável
    if (!autoStarted) return (
      <div style={{ ...barStyle, cursor: "pointer", justifyContent: "center", gap: 24 }} onClick={handleSplashClick}>
        {audioEl}
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ width:8, height:8, borderRadius:"50%", background:"#00ff87", boxShadow:"0 0 8px #00ff87", display:"inline-block" }} />
          <span style={{ fontSize:13, fontWeight:700, letterSpacing:3, color:"#fff" }}>RADIO<span style={{color:"#00ff87"}}>IN</span></span>
        </div>
        <div style={{ width:1, height:30, background:"rgba(0,255,135,0.2)" }} />
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 20px", background:"rgba(0,255,135,0.15)", border:"1px solid rgba(0,255,135,0.3)", borderRadius:8 }}>
          <span style={{ fontSize:18 }}>▶</span>
          <span style={{ fontSize:12, color:"#00ff87", fontWeight:700, letterSpacing:2 }}>TOQUE PARA INICIAR</span>
        </div>
        <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
      </div>
    );

    // Se autoStarted mas sem áudio ainda — mostra botão também
    if (!playing && !current) return (
      <div style={{ ...barStyle, cursor: "pointer", justifyContent: "center", gap: 24 }} onClick={togglePlay}>
        {audioEl}
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ width:8, height:8, borderRadius:"50%", background:"#00ff87", boxShadow:"0 0 8px #00ff87", display:"inline-block" }} />
          <span style={{ fontSize:13, fontWeight:700, letterSpacing:3, color:"#fff" }}>RADIO<span style={{color:"#00ff87"}}>IN</span></span>
        </div>
        <div style={{ width:1, height:30, background:"rgba(0,255,135,0.2)" }} />
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 20px", background:"rgba(0,255,135,0.15)", border:"1px solid rgba(0,255,135,0.3)", borderRadius:8 }}>
          <span style={{ fontSize:18 }}>▶</span>
          <span style={{ fontSize:12, color:"#00ff87", fontWeight:700, letterSpacing:2 }}>TOQUE PARA INICIAR</span>
        </div>
        <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
      </div>
    );

    // Player horizontal
    const trackLabel = current?.type === "announcement" || current?.type === "announcement_pool"
      ? "LOCUÇÃO" : (current?.category || "");

    return (
      <div style={{ ...barStyle, cursor: playing ? "default" : "pointer" }} onClick={!playing ? togglePlay : undefined}>
        {audioEl}
        {/* Overlay quando pausado */}
        {!playing && (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", gap:20, background:"rgba(5,8,16,0.92)", zIndex:10, cursor:"pointer" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ width:10, height:10, borderRadius:"50%", background:"#00ff87", boxShadow:"0 0 10px #00ff87", display:"inline-block" }} />
              <span style={{ fontSize:14, fontWeight:700, letterSpacing:4, color:"#fff" }}>RADIO<span style={{color:"#00ff87"}}>IN</span></span>
            </div>
            <div style={{ width:1, height:32, background:"rgba(0,255,135,0.25)" }} />
            <div style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 24px", background:"rgba(0,255,135,0.15)", border:"1px solid rgba(0,255,135,0.3)", borderRadius:10 }}>
              <span style={{ fontSize:20 }}>▶</span>
              <span style={{ fontSize:13, color:"#00ff87", fontWeight:700, letterSpacing:2 }}>TOQUE PARA INICIAR</span>
            </div>
          </div>
        )}

        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#00ff87", boxShadow: "0 0 10px #00ff87", display: "inline-block", animation: "blink 2s infinite" }} />
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: 4, color: "#fff" }}>RADIO<span style={{ color: "#00ff87" }}>IN</span></span>
        </div>

        {/* Separador */}
        <span style={{ width: 1, height: 36, background: "rgba(0,255,135,0.2)", flexShrink: 0 }} />

        {/* Visualizador */}
        <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0, height: 32 }}>
          {[0.6,1.0,0.7,0.9,0.5,0.8,0.6].map((h,i) => (
            <div key={i} style={{ width: 3, background: "#00ff87", borderRadius: 2, opacity: playing ? 0.8 : 0.25,
              height: playing ? `${Math.round(h*32)}px` : "6px",
              transition: "height 0.3s ease",
              animation: playing ? `vis${i} ${0.5+i*0.15}s ease-in-out infinite alternate` : "none"
            }} />
          ))}
        </div>

        {/* Separador */}
        <span style={{ width: 1, height: 36, background: "rgba(0,255,135,0.2)", flexShrink: 0 }} />

        {/* Info da música — ocupa o espaço disponível */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          {trackLabel && <span style={{ fontSize: 10, color: "rgba(0,255,135,0.7)", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>{trackLabel}</span>}
          <span style={{ fontSize: 15, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{current?.title || "—"}</span>
          {current?.subtitle && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{current.subtitle}</span>}
        </div>

        {/* Progresso */}
        <div style={{ width: 160, flexShrink: 0 }}>
          <div style={{ height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", background: "linear-gradient(90deg,#00ff87,#00d4ff)", borderRadius: 2, width: duration > 0 ? `${(currentTime/duration)*100}%` : "0%", transition: "width 1s linear" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>{formatTime(currentTime)}</span>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Separador */}
        <span style={{ width: 1, height: 36, background: "rgba(0,255,135,0.2)", flexShrink: 0 }} />

        {/* Controles */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <button onClick={goNext} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 18, cursor: "pointer", padding: 4 }}>⏮</button>
          <button onClick={togglePlay} style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#00ff87,#00d4ff)", border: "none", color: "#050810", fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {playing ? "⏸" : "▶"}
          </button>
          <button onClick={goNext} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 18, cursor: "pointer", padding: 4 }}>⏭</button>
        </div>

        <style>{`
          @keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}
          ${[0,1,2,3,4,5,6].map(i=>`@keyframes vis${i}{from{transform:scaleY(0.3)}to{transform:scaleY(1)}}`).join("")}
        `}</style>
      </div>
    );
  }

  if (loading) return (
    <div style={styles.root}>{audioEl}<div style={styles.loadingWrap}><div style={styles.spinner} /><p style={{ color: "rgba(255,255,255,0.4)", marginTop: 20, fontFamily: "monospace", letterSpacing: 3 }}>CARREGANDO</p></div><style>{spinnerCSS}</style></div>
  );
  if (error) return (
    <div style={styles.root}>{audioEl}<div style={styles.errorWrap}><div style={{ fontSize: 48, marginBottom: 16 }}>📻</div><p style={{ color: "#ff4466", fontFamily: "monospace", textAlign: "center", maxWidth: 400, lineHeight: 1.6 }}>{error}</p></div></div>
  );
  if (queue.length === 0) return (
    <div style={styles.root}>{audioEl}<div style={styles.errorWrap}><div style={{ fontSize: 48, marginBottom: 16 }}>🎙️</div><p style={{ color: "rgba(255,255,255,0.5)", fontFamily: "monospace", textAlign: "center" }}>Nenhuma grade configurada para<br /><span style={{ color: "#00ff87" }}>{store?.name}</span></p></div></div>
  );

  const isMusic = current?.type === "music";

  // Tela de splash — só aparece se autoplay foi bloqueado
  if (!autoStarted) return (
    <div style={{ ...styles.root, cursor: "pointer" }} onClick={handleSplashClick}>
      {audioEl}
      <div style={styles.glowA} /><div style={styles.glowB} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24, zIndex: 1 }}>
        <div style={styles.logo}>
          <span style={styles.logoDot} />
          <span style={styles.logoText}>RADIO<span style={{ color: "#00ff87" }}>IN</span></span>
        </div>
        <div style={styles.storeName}>{store?.name}</div>
        <div style={{ width: 100, height: 100, borderRadius: "50%", background: "linear-gradient(135deg,#00ff87,#00d4ff)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, boxShadow: "0 0 40px rgba(0,255,135,0.5)", animation: "pulse 2s ease-in-out infinite" }}>
          ▶
        </div>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", fontFamily: "monospace", letterSpacing: 2 }}>TOQUE PARA INICIAR</p>
      </div>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #050810; }
        @keyframes pulse { 0%,100%{transform:scale(1);box-shadow:0 0 40px rgba(0,255,135,0.5)} 50%{transform:scale(1.08);box-shadow:0 0 60px rgba(0,255,135,0.8)} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>
    </div>
  );

  return (
    <div style={styles.root}>
      {audioEl}
      <div style={styles.glowA} /><div style={styles.glowB} />

      <div style={styles.topBar}>
        <div style={styles.logo}><span style={styles.logoDot} /><span style={styles.logoText}>RADIO<span style={{ color: "#00ff87" }}>IN</span></span></div>
        <div style={styles.storeName}>{store?.name}</div>
        <div style={styles.storeType}>{store?.type?.toUpperCase()}</div>
      </div>

      <div style={{ ...styles.card, transform: pulse ? "scale(1.02)" : "scale(1)", transition: "transform 0.3s ease" }}>
        <div style={styles.ringWrap}>
          <ProgressRing progress={progress} size={220} stroke={5} />
          <div style={{ ...styles.ringInner, border: `1px solid ${isMusic ? "rgba(0,212,255,0.2)" : "rgba(0,255,135,0.2)"}` }}>
            <EqualizerBars playing={playing} />
          </div>
        </div>

        <div style={styles.trackInfo}>
          <div style={{ fontSize: 11, letterSpacing: 2, color: isMusic ? "rgba(0,212,255,0.7)" : "rgba(0,255,135,0.7)", marginBottom: 6, textTransform: "uppercase" }}>
            {isMusic ? `🎵 ${current?.albumName || "Música"}` : "🎙 Locução"}
          </div>
          <div style={styles.trackTitle}>{current?.title}</div>
          {!isMusic && current?.text && <div style={styles.trackText}>{current.text}</div>}
          <div style={styles.trackMeta}>
            <span style={{ ...styles.voiceBadge, background: isMusic ? "rgba(0,212,255,0.1)" : "rgba(0,255,135,0.1)", border: `1px solid ${isMusic ? "rgba(0,212,255,0.2)" : "rgba(0,255,135,0.2)"}`, color: isMusic ? "#00d4ff" : "#00ff87" }}>
              {isMusic ? "♪" : "🎙"} {current?.subtitle}
            </span>
            <span style={styles.counter}>{currentIndex + 1} / {queue.length}</span>
          </div>
        </div>

        <div style={styles.timeRow}>
          <span style={styles.timeText}>{fmt(currentTime)}</span>
          <div style={styles.progressBar}><div style={{ ...styles.progressFill, width: `${progress}%`, background: isMusic ? "linear-gradient(to right, #00d4ff, #00ff87)" : "linear-gradient(to right, #00ff87, #00d4ff)" }} /></div>
          <span style={styles.timeText}>{fmt(duration)}</span>
        </div>

        <div style={styles.controls}>
          <button onClick={playPrev} style={styles.btnSm}>⏮</button>
          <button onClick={togglePlay} style={styles.btnPlay}>{playing ? "⏸" : "▶"}</button>
          <button onClick={playNext} style={styles.btnSm}>⏭</button>
        </div>

        <div style={styles.volumeRow}>
          <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 14 }}>🔈</span>
          <input type="range" min={0} max={1} step={0.01} value={volume} onChange={e => { setVolume(parseFloat(e.target.value)); sendPlayerState(); }} style={styles.slider} />
          <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 14 }}>🔊</span>
        </div>
      </div>

      <div style={styles.playlist}>
        {queue.map((item, i) => (
          <div key={item.id + i}
            onClick={() => {
              // Bloco pool não é clicável diretamente — o goNext resolve a API
              if (item.type === "announcement_pool") return;
              setCurrentIndex(i); currentIndexRef.current = i;
            }}
            style={{ ...styles.playlistItem,
              background: i === currentIndex
                ? (item.type === "music" ? "rgba(0,212,255,0.08)" : "rgba(0,255,135,0.08)")
                : "rgba(255,255,255,0.03)",
              borderLeft: i === currentIndex
                ? `3px solid ${item.type === "music" ? "#00d4ff" : "#00ff87"}`
                : "3px solid transparent",
              cursor: item.type === "announcement_pool" ? "default" : "pointer",
              opacity: item.type === "announcement_pool" ? 0.7 : 1,
            }}>
            <span style={{ fontSize: 14 }}>
              {item.type === "music" ? "🎵" : item.type === "announcement_pool" ? "🔄" : "🎙"}
            </span>
            <span style={{ color: i === currentIndex ? "#fff" : "rgba(255,255,255,0.5)", fontSize: 13, flex: 1 }}>
              {item.type === "announcement_pool" ? (item.title || "Locuções (rodízio)") : item.title}
            </span>
            <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 11 }}>{item.meta}</span>
          </div>
        ))}
      </div>

      {!isOnline && (
        <div style={{ marginTop: 16, zIndex: 1, display: "flex", alignItems: "center", gap: 8, background: "rgba(255,68,68,0.15)", border: "1px solid rgba(255,68,68,0.3)", borderRadius: 20, padding: "6px 14px" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#ff4444" }} />
          <span style={{ fontSize: 10, color: "#ff6666", fontFamily: "monospace", letterSpacing: 1 }}>
            SEM CONEXÃO — {cachedCount > 0 ? `${cachedCount} FAIXAS NO CACHE` : "AGUARDANDO..."}
          </span>
        </div>
      )}

      <div style={{ marginTop: 16, zIndex: 1, display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#00ff87", opacity: 0.5, animation: "blink 2s ease-in-out infinite" }} />
        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", fontFamily: "monospace", letterSpacing: 1 }}>SYNC EM {fmtCountdown(reloadCountdown)}</span>
      </div>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #050810; }
        input[type=range] { -webkit-appearance: none; height: 3px; border-radius: 2px; background: rgba(255,255,255,0.1); outline: none; cursor: pointer; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 14px; height: 14px; border-radius: 50%; background: #00ff87; cursor: pointer; }
        ${spinnerCSS}
      `}</style>
    </div>
  );
}

const styles = {
  root: { minHeight: "100vh", background: "#050810", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 16px", position: "relative", overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" },
  glowA: { position: "fixed", top: "-20%", left: "20%", width: 500, height: 500, background: "radial-gradient(circle, rgba(0,255,135,0.12) 0%, transparent 70%)", pointerEvents: "none" },
  glowB: { position: "fixed", bottom: "-20%", right: "10%", width: 400, height: 400, background: "radial-gradient(circle, rgba(0,212,255,0.1) 0%, transparent 70%)", pointerEvents: "none" },
  topBar: { display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 32, zIndex: 1 },
  logo: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12 },
  logoDot: { width: 8, height: 8, borderRadius: "50%", background: "#00ff87", boxShadow: "0 0 12px #00ff87", animation: "blink 2s ease-in-out infinite" },
  logoText: { fontSize: 13, fontWeight: 700, letterSpacing: 6, color: "rgba(255,255,255,0.7)" },
  storeName: { fontSize: 28, fontWeight: 700, color: "#fff", letterSpacing: 1, textAlign: "center" },
  storeType: { fontSize: 12, color: "rgba(255,255,255,0.3)", letterSpacing: 3, marginTop: 4 },
  card: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 24, padding: "40px 32px 32px", width: "100%", maxWidth: 420, backdropFilter: "blur(20px)", display: "flex", flexDirection: "column", alignItems: "center", gap: 24, zIndex: 1, boxShadow: "0 32px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)" },
  ringWrap: { position: "relative", width: 220, height: 220, display: "flex", alignItems: "center", justifyContent: "center" },
  ringInner: { position: "relative", zIndex: 1, width: 160, height: 160, borderRadius: "50%", background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" },
  trackInfo: { textAlign: "center", width: "100%" },
  trackTitle: { fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 8, letterSpacing: 0.5 },
  trackText: { fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.5, marginBottom: 12, maxHeight: 60, overflow: "hidden" },
  trackMeta: { display: "flex", alignItems: "center", justifyContent: "center", gap: 16 },
  voiceBadge: { fontSize: 11, borderRadius: 20, padding: "3px 10px", letterSpacing: 1 },
  counter: { fontSize: 11, color: "rgba(255,255,255,0.25)", letterSpacing: 2 },
  timeRow: { display: "flex", alignItems: "center", gap: 10, width: "100%" },
  timeText: { fontSize: 11, color: "rgba(255,255,255,0.3)", fontVariantNumeric: "tabular-nums", minWidth: 32, fontFamily: "monospace" },
  progressBar: { flex: 1, height: 3, borderRadius: 2, background: "rgba(255,255,255,0.08)", overflow: "hidden" },
  progressFill: { height: "100%", borderRadius: 2, transition: "width 0.3s ease" },
  controls: { display: "flex", alignItems: "center", gap: 24 },
  btnSm: { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "50%", width: 44, height: 44, color: "rgba(255,255,255,0.6)", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  btnPlay: { background: "linear-gradient(135deg, #00ff87, #00d4ff)", border: "none", borderRadius: "50%", width: 64, height: 64, color: "#050810", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 30px rgba(0,255,135,0.4)", fontWeight: 700 },
  volumeRow: { display: "flex", alignItems: "center", gap: 10, width: "100%" },
  slider: { flex: 1 },
  playlist: { width: "100%", maxWidth: 420, marginTop: 16, zIndex: 1, display: "flex", flexDirection: "column", gap: 4 },
  playlistItem: { display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderRadius: 10, cursor: "pointer", transition: "all 0.2s" },
  loadingWrap: { display: "flex", flexDirection: "column", alignItems: "center" },
  errorWrap: { display: "flex", flexDirection: "column", alignItems: "center", padding: 40 },
  spinner: { width: 40, height: 40, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.1)", borderTop: "3px solid #00ff87", animation: "spin 0.8s linear infinite" },
};

const spinnerCSS = `
  @keyframes spin { to { transform: rotate(360deg) } }
  @keyframes blink { 0%,100% { opacity:1 } 50% { opacity:0.3 } }
`;
